import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form as CommonForm, Button, Spinner, Paragraph, Input, SelectBox, Toggle } from 'Common';
import { TEXT as FORM_TEXT } from 'locales/landingpages/insuranceCompany';
import { TEXT } from 'locales/translations';
import { getCurrentUser, isGuest, isRegisterHealthInsuranceLoading, isValidateInsuranceNumberFailed } from 'redux/query';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { isBirthday, isValidEmail, isValidInsuranceNumber, validOptionalInsuranceNumbersLength, isValidPostalCode } from 'utils/validators';
import { dateStringToDate, toIsoDate } from 'utils';
import { registerUserWithInsuranceNumberRequest } from 'redux/actions';
import { useGetHealthInsuranceCooperation } from 'hookApi';
import { InsuranceNumberAlreadyExistsButton, HealthRequirementsHint, PrivacyPolicyHint } from 'Component';

export const Form = ({ children, disabled = false, onSubmit, fieldsOverride = {}, params = {} }) => {
  const dispatch = useDispatch();

  const { formText } = FORM_TEXT;

  const user = useSelector(getCurrentUser);
  const isGuestUser = useSelector(isGuest);
  const isInsuranceNumberFailed = useSelector(isValidateInsuranceNumberFailed);

  const { selectedOffer } = useGetHealthInsuranceCooperation(params);

  const formFields = useMemo(() => selectedOffer?.form?.fields || {}, [selectedOffer]);
  const defaultValues = useMemo(() => selectedOffer?.form?.defaultValues, [selectedOffer]);
  const optionalFields = useMemo(() => selectedOffer?.form?.optionalFields, [selectedOffer]);
  const withoutInsuranceNumber = useMemo(
    () => !selectedOffer?.form?.fields?.insuranceNumber || !!optionalFields?.insuranceNumber,
    [selectedOffer, optionalFields]
  );

  const [formState, setFormState] = useState({
    name: isGuestUser ? '' : user.firstName,
    email: isGuestUser ? '' : user.email,
    healthCheck: false,
    termsCheck: false,
    ...defaultValues
  });

  const {
    register: _register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: isGuestUser ? '' : user.firstName,
      email: isGuestUser ? '' : user.email,
      healthCheck: false,
      termsCheck: false,
      ...defaultValues
    },
    mode: 'onBlur'
  });

  const isLoading = useSelector(isRegisterHealthInsuranceLoading);
  const isDisabled = useMemo(() => {
    for (const key in formState) {
      const value = formState[key];
      if (key === 'insuranceNumber' && withoutInsuranceNumber && !value) {
        continue;
      }
      if (errors?.[key] || (!value && !optionalFields?.[key])) {
        return true;
      }
    }
    return false;
  }, [formState, errors, optionalFields]);

  const handleOnSubmit = async (evt) => {
    evt.preventDefault();
    if (isLoading) {
      return;
    }

    if (onSubmit) {
      return await handleSubmit(onSubmit)(evt);
    }
    await handleSubmit(registerUser)(evt);
  };

  useEffect(() => {
    if (isInsuranceNumberFailed) {
      setError('insuranceNumber', {
        type: 'manual',
        message: TEXT.formsGeneral.insuranceNumberAlreadyExists
      });
    }
  }, [isInsuranceNumberFailed, setError]);

  const register = useCallback(
    (name, rules = {}) => {
      const _additionalAttributes = {};
      const _rules = {
        ...rules,
        disabled,
        onChange: (evt) => {
          if (evt && evt.target) {
            switch (true) {
              case evt.target.type === 'checkbox': {
                setFormState((prevState) => ({ ...prevState, [name]: evt.target.checked }));
                break;
              }
              case 'value' in evt.target: {
                setFormState((prevState) => ({ ...prevState, [name]: evt.target.value }));
                break;
              }
            }
          }
          rules.onChange && rules.onChange(evt);
          clearErrors(name);
        }
      };

      switch (name) {
        case 'name': {
          _rules.required = TEXT.formsGeneral.requiredName;
          break;
        }
        case 'email': {
          _rules.required = TEXT.formsGeneral.requiredEmail;
          _rules.validate = {
            validMail: (value) => isValidEmail(value) || TEXT.formsGeneral.validEmail
          };
          break;
        }
        case 'insuranceNumber': {
          _rules.required = TEXT.formsGeneral.validLengthNum;
          let validationRuleFn = isValidInsuranceNumber;

          if (withoutInsuranceNumber) {
            validationRuleFn = validOptionalInsuranceNumbersLength;
            _rules.required = false;
          }

          _rules.validate = {
            validInsuranceNumber: (value) => validationRuleFn(value) || TEXT.formsGeneral.validInsuranceNumber
          };

          break;
        }
        case 'dateOfBirth': {
          _rules.required = TEXT.formsGeneral.requiredDateOfBirth;
          if (!formFields?.dateOfBirth?.required) {
            _rules.required = false;
          }
          _rules.validate = {
            invalidDate: (value) =>
              !!value.length ? dateStringToDate(value).toString() !== 'Invalid Date' || TEXT.formsGeneral.validDateOfBirth : true,
            invalidBirthDate: (value) => (!!value.length ? isBirthday(value) || TEXT.formsGeneral.validDateOfBirth : true)
          };
          break;
        }
        case 'postalCode': {
          if (formFields?.postalCode?.required) {
            _rules.required = TEXT.formsGeneral.requiredPostalCode;
          }
          _rules.validate = {
            validPostcode: (value) => isValidPostalCode(value) || TEXT.formsGeneral.validPostalCode
          };
          break;
        }
        case 'healthCheck': {
          _rules.required = TEXT.formsGeneral.requiredHealthCheck;
          break;
        }
        case 'termsCheck': {
          _rules.required = TEXT.formsGeneral.requiredTermsCheck;
          break;
        }
        case 'personalDataConditionAccepted': {
          _rules.required = TEXT.formsGeneral.validPersonalDataSync;
          break;
        }
        case 'postalCodeCheck': {
          _rules.required = TEXT.formsGeneral.validPostalCodeCheck;
          break;
        }
        case 'precondition': {
          _rules.required = TEXT.formsGeneral.requiredPrecondition;
          break;
        }
        case 'courseParticipationLimitAccepted': {
          _rules.required = TEXT.formsGeneral.requiredCourseParticipationLimitAccepted;
          break;
        }
        case 'costCoverageConditionAccepted': {
          _rules.required = TEXT.formsGeneral.requiredCostCoverageConditionAccepted;
          break;
        }
        case 'gender': {
          if (!!rules.required || formFields?.gender?.required) {
            _rules.required = typeof rules.required === 'string' ? rules.required : TEXT.formsGeneral.requiredGender;
          }
          _additionalAttributes.options = [
            { value: 'FEMALE', label: `${TEXT.genderOptions.female}` },
            { value: 'MALE', label: `${TEXT.genderOptions.male}` },
            { value: 'DIVERS', label: `${TEXT.genderOptions.divers}` }
          ];
          break;
        }
        case 'age': {
          if (!!rules.required) {
            _rules.required = typeof rules.required === 'string' ? rules.required : TEXT.formsGeneral.requiredAge;
          }
          _rules.max =
            typeof rules.max === 'boolean'
              ? {
                  value: 120,
                  message: TEXT.formsGeneral.ageToHigh
                }
              : rules.max;
          _rules.min =
            typeof rules.min === 'boolean'
              ? {
                  value: 14,
                  message: TEXT.formsGeneral.ageToLow
                }
              : rules.max;

          break;
        }
      }

      return {
        ..._register(name, _rules),
        ..._additionalAttributes,
        key: `form-input-field-${name}`,
        feedbackMessage: errors[name]?.message,
        valid: !!errors[name] ? false : undefined,
        dataCyName: `${name}-input`
      };
    },
    [errors, disabled, formFields]
  );

  const registerUser = () => {
    const { name = '', email = '', insuranceNumber = '', ...userMetaData } = formState;
    const _userMetaData = { ...userMetaData };
    if (formState.dateOfBirth) {
      _userMetaData.dateOfBirth = toIsoDate(formState.dateOfBirth);
    }
    Object.keys(formState).forEach((key) => {
      const value = formState[key];
      if (!value && defaultValues[key]) {
        formState[key] = defaultValues[key];
      }
    });

    dispatch(
      registerUserWithInsuranceNumberRequest({
        user: { name, email, insuranceNumber },
        // Override userMetaData transformations
        userMetaData: _userMetaData,
        sendDeepLinkOTP: false,
        optionalInsuranceNumber: withoutInsuranceNumber
      })
    );
  };

  const renderFormField = ({ fieldName, render }) => {
    if (!['name', 'email'].includes(fieldName) && !formFields?.[fieldName]) {
      return null;
    }
    if (fieldsOverride?.[fieldName]) {
      return fieldsOverride[fieldName]({ register });
    }
    if (render) {
      return render();
    }
    return (
      <Input
        {...register(fieldName)}
        placeholder={formText.placeholder?.[`${fieldName}${formFields?.[fieldName]?.required ? 'Req' : ''}`]}
      />
    );
  };

  return (
    <div className="insurance-form-wrapper" data-cy-name="landingpage-register-form">
      <CommonForm className="form" onSubmit={handleOnSubmit}>
        {!!children ? (
          children({ register, values: formState, errors })
        ) : (
          <>
            {renderFormField({ fieldName: 'name' })}
            {renderFormField({
              fieldName: 'email',
              render: () => <Input {...register('email')} placeholder={formText.placeholder.email} type="email" />
            })}
            {renderFormField({
              fieldName: 'insuranceNumber',
              render: () => (
                <>
                  <Input
                    {...register('insuranceNumber')}
                    placeholder={formText.placeholder[`insuranceNumber${formFields.insuranceNumber.required ? 'Req' : ''}`]}
                  />
                  <InsuranceNumberAlreadyExistsButton values={formState} />
                  <Paragraph className="extra-small">{formText.insuranceNumberInfo}</Paragraph>
                </>
              )
            })}
            {renderFormField({ fieldName: 'companyName' })}
            {renderFormField({ fieldName: 'postalCode' })}
            {renderFormField({ fieldName: 'dateOfBirth' })}
            {renderFormField({ fieldName: 'age' })}
            {renderFormField({
              fieldName: 'gender',
              render: () => (
                <SelectBox {...register('gender')} placeholder={formText.placeholder[`gender${formFields.gender.required ? 'Req' : ''}`]} />
              )
            })}
            <Toggle {...register('healthCheck')} className="mb-3" id="health-terms-toggle">
              <HealthRequirementsHint />
            </Toggle>
            <Toggle {...register('termsCheck')} className="mb-3" id="privacy-toggle">
              <PrivacyPolicyHint />
            </Toggle>
            {!!formFields?.personalDataConditionAccepted && (
              <Toggle
                {...register('personalDataConditionAccepted')}
                className="mb-3"
                id="personal-sync-toggle"
                dataCyName="personalDataConditionAccepted-input"
              >
                <Paragraph className="extra-small personal-data-text">{formText.personalDataSync}</Paragraph>
              </Toggle>
            )}
            {!!formFields?.courseParticipationLimitAccepted && (
              <Toggle
                {...register('courseParticipationLimitAccepted')}
                className="mb-3"
                id="course-participationL-lmit-accepted"
                dataCyName="courseParticipationLimitAccepted-input"
              >
                <Paragraph className="extra-small personal-data-text">{formText.courseParticipationLimitAccepted}</Paragraph>
              </Toggle>
            )}
            {!!formFields?.costCoverageConditionAccepted && (
              <Toggle
                {...register('costCoverageConditionAccepted')}
                className="mb-3"
                id="cost-coverage-condition-accepted"
                dataCyName="costCoverageConditionAccepted-input"
              >
                <Paragraph className="extra-small personal-data-text">{formText.costCoverageConditionAccepted}</Paragraph>
              </Toggle>
            )}
          </>
        )}
        <Paragraph className="extra-small legal-newsletter-info">{TEXT.formsGeneral.legalNewsletterInfo}</Paragraph>
        <Button type="submit" color="green" dataCyName="submit-register-button" disabled={isDisabled}>
          {isLoading ? <Spinner /> : TEXT.registration.register_btn}
        </Button>
      </CommonForm>
    </div>
  );
};
